import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  MessageSquare,
  Mic,
  Volume2,
  ChevronRight,
  ChevronLeft,
  Check,
  Sparkles,
  MemoryStick,
} from 'lucide-react';
import {
  NeMoPretrainedModel,
  NeMoDataset,
  NeMoTaskType,
  NeMoTrainingMethod,
  NeMoTrainingConfig,
  NeMoLoRAConfig as LoRAConfigType,
  getTrainingMethodLabel,
  getTrainingMethodDescription,
} from '@/mock/gpu-instance';
import NeMoModelSelector from './NeMoModelSelector';
import NeMoModelCard from './NeMoModelCard';
import NeMoDatasetManager from './NeMoDatasetManager';
import NeMoConfigBuilder from './NeMoConfigBuilder';

interface NeMoExperimentCreatorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  models: NeMoPretrainedModel[];
  datasets: NeMoDataset[];
  onCreateExperiment: (experiment: {
    name: string;
    task: NeMoTaskType;
    method: NeMoTrainingMethod;
    model: NeMoPretrainedModel;
    dataset: NeMoDataset;
    config: NeMoTrainingConfig;
  }) => void;
}

const STEPS = [
  { id: 1, title: 'Task', description: 'Select training task' },
  { id: 2, title: 'Method', description: 'Choose training method' },
  { id: 3, title: 'Model', description: 'Select pretrained model' },
  { id: 4, title: 'Dataset', description: 'Choose training dataset' },
  { id: 5, title: 'Config', description: 'Configure training' },
  { id: 6, title: 'Review', description: 'Review and create' },
];

const TASKS: { id: NeMoTaskType; name: string; description: string; icon: typeof MessageSquare }[] = [
  {
    id: 'llm',
    name: 'LLM Fine-tuning',
    description: 'Fine-tune large language models like Llama, Mistral, Gemma, Qwen',
    icon: MessageSquare,
  },
  {
    id: 'asr',
    name: 'ASR Training',
    description: 'Train speech recognition models like Parakeet, Canary, Conformer',
    icon: Mic,
  },
  {
    id: 'tts',
    name: 'TTS Training',
    description: 'Train text-to-speech models like FastPitch, VITS, RAD-TTS',
    icon: Volume2,
  },
];

const METHODS: { id: NeMoTrainingMethod; name: string; description: string; vramLevel: string; recommended?: boolean }[] = [
  {
    id: 'lora',
    name: 'LoRA',
    description: 'Low-Rank Adaptation - train small adapter weights',
    vramLevel: 'Low',
    recommended: true,
  },
  {
    id: 'qlora',
    name: 'QLoRA',
    description: '4-bit quantized LoRA for maximum memory efficiency',
    vramLevel: 'Very Low',
  },
  {
    id: 'sft',
    name: 'Supervised Fine-Tuning',
    description: 'Standard fine-tuning with frozen layers',
    vramLevel: 'Medium',
  },
  {
    id: 'ptuning',
    name: 'P-Tuning v2',
    description: 'Prompt tuning with learnable soft prompts',
    vramLevel: 'Low',
  },
  {
    id: 'full',
    name: 'Full Fine-tuning',
    description: 'Train all model parameters (highest quality)',
    vramLevel: 'Very High',
  },
];

const getDefaultLoRAConfig = (): LoRAConfigType => ({
  rank: 16,
  alpha: 32,
  dropout: 0.05,
  targetModules: ['q_proj', 'k_proj', 'v_proj', 'o_proj'],
});

const getDefaultConfig = (task: NeMoTaskType, method: NeMoTrainingMethod): NeMoTrainingConfig => {
  const baseConfig: NeMoTrainingConfig = {
    method,
    epochs: task === 'llm' ? 3 : 50,
    batchSize: task === 'llm' ? 4 : 16,
    learningRate: method === 'lora' || method === 'qlora' ? 2e-4 : 1e-4,
    optimizer: 'adamw',
    scheduler: 'warmup_cosine',
  };

  if (task === 'llm') {
    baseConfig.sequenceLength = 4096;
    baseConfig.sequencePacking = true;
  }

  if (task === 'asr') {
    baseConfig.specAugment = true;
    baseConfig.speedPerturbation = false;
    baseConfig.decoderType = 'ctc';
  }

  if (task === 'tts') {
    baseConfig.sampleRate = 22050;
    baseConfig.speakerEmbedding = false;
  }

  if (method === 'lora' || method === 'qlora') {
    baseConfig.loraConfig = getDefaultLoRAConfig();
    if (method === 'qlora') {
      baseConfig.loraConfig.quantization = '4bit';
    }
  }

  return baseConfig;
};

const NeMoExperimentCreator = ({
  open,
  onOpenChange,
  models,
  datasets,
  onCreateExperiment,
}: NeMoExperimentCreatorProps) => {
  const [step, setStep] = useState(1);
  const [experimentName, setExperimentName] = useState('');
  const [selectedTask, setSelectedTask] = useState<NeMoTaskType | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<NeMoTrainingMethod | null>(null);
  const [selectedModel, setSelectedModel] = useState<NeMoPretrainedModel | null>(null);
  const [selectedDataset, setSelectedDataset] = useState<NeMoDataset | null>(null);
  const [config, setConfig] = useState<NeMoTrainingConfig | null>(null);
  const [modelCardOpen, setModelCardOpen] = useState(false);
  const [modelCardModel, setModelCardModel] = useState<NeMoPretrainedModel | null>(null);

  const resetState = () => {
    setStep(1);
    setExperimentName('');
    setSelectedTask(null);
    setSelectedMethod(null);
    setSelectedModel(null);
    setSelectedDataset(null);
    setConfig(null);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetState();
    }
    onOpenChange(isOpen);
  };

  const handleNext = () => {
    if (step === 2 && selectedTask && selectedMethod) {
      // Initialize config when moving from method selection
      setConfig(getDefaultConfig(selectedTask, selectedMethod));
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleCreate = () => {
    if (!selectedTask || !selectedMethod || !selectedModel || !selectedDataset || !config) return;

    const name = experimentName || `${selectedModel.family.toLowerCase()}_${selectedTask}_${Date.now().toString(36)}`;

    onCreateExperiment({
      name,
      task: selectedTask,
      method: selectedMethod,
      model: selectedModel,
      dataset: selectedDataset,
      config,
    });

    handleOpenChange(false);
  };

  const handleViewModelCard = (model: NeMoPretrainedModel) => {
    setModelCardModel(model);
    setModelCardOpen(true);
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return selectedTask !== null;
      case 2:
        return selectedMethod !== null;
      case 3:
        return selectedModel !== null;
      case 4:
        return selectedDataset !== null;
      case 5:
        return config !== null;
      case 6:
        return true;
      default:
        return false;
    }
  };

  const getTaskIcon = (task: NeMoTaskType) => {
    switch (task) {
      case 'llm':
        return <MessageSquare className="h-5 w-5 text-green-500" />;
      case 'asr':
        return <Mic className="h-5 w-5 text-blue-500" />;
      case 'tts':
        return <Volume2 className="h-5 w-5 text-purple-500" />;
    }
  };

  // Filter models and datasets by selected task
  const filteredModels = selectedTask
    ? models.filter((m) => m.task === selectedTask)
    : models;
  const filteredDatasets = selectedTask
    ? datasets.filter((d) => d.task === selectedTask)
    : datasets;

  // Filter methods by selected model
  const availableMethods = selectedTask === 'llm'
    ? METHODS
    : METHODS.filter((m) => m.id !== 'ptuning'); // P-Tuning only for LLM

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create NeMo Experiment</DialogTitle>
          <DialogDescription>
            Step {step} of {STEPS.length}: {STEPS[step - 1].description}
          </DialogDescription>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-6">
          {STEPS.map((s, idx) => (
            <div key={s.id} className="flex items-center">
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                  step > s.id
                    ? 'bg-primary text-primary-foreground'
                    : step === s.id
                    ? 'bg-primary/10 text-primary border-2 border-primary'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {step > s.id ? <Check className="h-4 w-4" /> : s.id}
              </div>
              {idx < STEPS.length - 1 && (
                <div
                  className={`w-8 h-0.5 mx-1 ${
                    step > s.id ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="min-h-[400px]">
          {/* Step 1: Task Selection */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                {TASKS.map((task) => {
                  const Icon = task.icon;
                  const isSelected = selectedTask === task.id;
                  return (
                    <Card
                      key={task.id}
                      className={`cursor-pointer transition-all hover:border-primary/50 ${
                        isSelected ? 'border-primary ring-1 ring-primary' : ''
                      }`}
                      onClick={() => {
                        setSelectedTask(task.id);
                        setSelectedModel(null);
                        setSelectedDataset(null);
                      }}
                    >
                      <CardContent className="pt-4 flex items-center gap-4">
                        <div className={`p-3 rounded-lg ${
                          task.id === 'llm' ? 'bg-green-500/10' :
                          task.id === 'asr' ? 'bg-blue-500/10' : 'bg-purple-500/10'
                        }`}>
                          <Icon className={`h-6 w-6 ${
                            task.id === 'llm' ? 'text-green-500' :
                            task.id === 'asr' ? 'text-blue-500' : 'text-purple-500'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium flex items-center gap-2">
                            {task.name}
                            {isSelected && <Check className="h-4 w-4 text-primary" />}
                          </div>
                          <div className="text-sm text-muted-foreground">{task.description}</div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Training Method Selection */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3">
                {availableMethods.map((method) => {
                  const isSelected = selectedMethod === method.id;
                  const isSupported = selectedModel
                    ? selectedModel.supportedMethods.includes(method.id)
                    : true;
                  return (
                    <Card
                      key={method.id}
                      className={`cursor-pointer transition-all ${
                        isSelected
                          ? 'border-primary ring-1 ring-primary'
                          : isSupported
                          ? 'hover:border-primary/50'
                          : 'opacity-50 cursor-not-allowed'
                      }`}
                      onClick={() => isSupported && setSelectedMethod(method.id)}
                    >
                      <CardContent className="pt-4 flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${
                          method.id === 'lora' || method.id === 'qlora'
                            ? 'bg-green-500/10'
                            : 'bg-muted'
                        }`}>
                          <Sparkles className={`h-5 w-5 ${
                            method.id === 'lora' || method.id === 'qlora'
                              ? 'text-green-500'
                              : 'text-muted-foreground'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium flex items-center gap-2">
                            {method.name}
                            {method.recommended && (
                              <Badge variant="secondary" className="text-xs">Recommended</Badge>
                            )}
                            {isSelected && <Check className="h-4 w-4 text-primary" />}
                          </div>
                          <div className="text-sm text-muted-foreground">{method.description}</div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-1 text-sm">
                            <MemoryStick className="h-4 w-4" />
                            {method.vramLevel}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: Model Selection */}
          {step === 3 && (
            <NeMoModelSelector
              models={filteredModels}
              selectedModelId={selectedModel?.id}
              onSelectModel={setSelectedModel}
              onViewModelCard={handleViewModelCard}
              filterTask={selectedTask || undefined}
              filterMethod={selectedMethod || undefined}
            />
          )}

          {/* Step 4: Dataset Selection */}
          {step === 4 && (
            <NeMoDatasetManager
              datasets={filteredDatasets}
              selectedDatasetId={selectedDataset?.id}
              onSelectDataset={setSelectedDataset}
              filterTask={selectedTask || undefined}
            />
          )}

          {/* Step 5: Configuration */}
          {step === 5 && config && (
            <NeMoConfigBuilder
              config={config}
              onChange={setConfig}
              selectedModel={selectedModel || undefined}
              selectedTask={selectedTask || undefined}
            />
          )}

          {/* Step 6: Review */}
          {step === 6 && (
            <div className="space-y-4">
              {/* Experiment Name */}
              <div className="space-y-2">
                <Label>Experiment Name (optional)</Label>
                <Input
                  placeholder={`${selectedModel?.family.toLowerCase()}_${selectedTask}_experiment`}
                  value={experimentName}
                  onChange={(e) => setExperimentName(e.target.value)}
                />
              </div>

              {/* Summary */}
              <Card>
                <CardContent className="pt-4 space-y-4">
                  <div className="text-sm font-medium">Experiment Summary</div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Task</div>
                      <div className="flex items-center gap-2 font-medium">
                        {selectedTask && getTaskIcon(selectedTask)}
                        {TASKS.find((t) => t.id === selectedTask)?.name}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Training Method</div>
                      <div className="font-medium flex items-center gap-2">
                        {selectedMethod && getTrainingMethodLabel(selectedMethod)}
                        {(selectedMethod === 'lora' || selectedMethod === 'qlora') && (
                          <Badge className="bg-green-500/10 text-green-600 border-green-500/30 text-xs">
                            <Sparkles className="h-3 w-3 mr-1" />
                            Parameter Efficient
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Model</div>
                      <div className="font-medium">{selectedModel?.displayName}</div>
                      <div className="text-xs text-muted-foreground">
                        {selectedModel?.parameters} params
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Dataset</div>
                      <div className="font-medium">{selectedDataset?.displayName}</div>
                      <div className="text-xs text-muted-foreground">
                        {selectedDataset?.trainCount.toLocaleString()} train samples
                      </div>
                    </div>
                  </div>

                  {/* Config Summary */}
                  {config && (
                    <div className="pt-4 border-t">
                      <div className="text-sm text-muted-foreground mb-2">Training Configuration</div>
                      <div className="grid grid-cols-4 gap-2 text-sm">
                        <div>
                          <div className="text-muted-foreground">Epochs</div>
                          <div className="font-mono">{config.epochs}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Batch Size</div>
                          <div className="font-mono">{config.batchSize}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Learning Rate</div>
                          <div className="font-mono">{config.learningRate}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Optimizer</div>
                          <div className="font-mono capitalize">{config.optimizer}</div>
                        </div>
                        {config.loraConfig && (
                          <>
                            <div>
                              <div className="text-muted-foreground">LoRA Rank</div>
                              <div className="font-mono">{config.loraConfig.rank}</div>
                            </div>
                            <div>
                              <div className="text-muted-foreground">LoRA Alpha</div>
                              <div className="font-mono">{config.loraConfig.alpha}</div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* VRAM Estimate */}
                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-muted-foreground">Estimated VRAM Usage</div>
                      <div className="flex items-center gap-2">
                        <MemoryStick className="h-4 w-4" />
                        <span className="font-mono font-medium">
                          {(selectedMethod === 'lora' || selectedMethod === 'qlora')
                            ? `~${selectedModel?.vramLoRA} GB`
                            : `~${selectedModel?.vramRequired} GB`}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={handleBack} disabled={step === 1}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          {step < 6 ? (
            <Button onClick={handleNext} disabled={!canProceed()}>
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleCreate} disabled={!canProceed()}>
              <Check className="h-4 w-4 mr-1" />
              Create Experiment
            </Button>
          )}
        </div>
      </DialogContent>

      {/* Model Card Modal */}
      <NeMoModelCard
        model={modelCardModel}
        open={modelCardOpen}
        onOpenChange={setModelCardOpen}
        onSelectModel={(model) => {
          setSelectedModel(model);
          setModelCardOpen(false);
        }}
      />
    </Dialog>
  );
};

export default NeMoExperimentCreator;
